import React, { useCallback } from 'react';
import * as R from 'ramda';
import { useFormikContext } from 'formik';
import { MenuItem, Menu } from '@blueprintjs/core';
import styled from 'styled-components';
import intl from 'react-intl-universal';
import withDrawerActions from '@/containers/Drawer/withDrawerActions';
import { FSelect } from '../Forms';
import { useCreateAutofillListener } from '@/hooks/state/autofill';
import { DRAWERS } from '@/constants/drawers';
import { FormattedMessage as T } from '@/components/FormattedMessage';

/**
 * Vendor select.
 * @returns {React.ReactNode}
 */
function VendorsSelectRoot({
  // #withDrawerActions
  openDrawer,

  // #ownProps
  name,
  items,
  allowCreate,

  ...restProps
}: any) {
  const { setFieldValue } = useFormikContext();

  // Creates a new autofill listener once the quick vendor drawer submits the form.
  const autofillRef = useCreateAutofillListener((payload: any) => {
    setFieldValue(name, payload.vendorId);
  });

  // Handles the create item click.
  const handleCreateItemClick = useCallback(() => {
    openDrawer(DRAWERS.QUICK_WRITE_VENDOR, { autofillRef, displayName: '' });
  }, [openDrawer, autofillRef]);

  // Custom item list renderer with scrollable items and fixed "Create new" at bottom
  const itemListRenderer = useCallback(
    ({ items, itemsParentRef, renderItem }: any) => {
      const renderedItems = items
        .map(renderItem)
        .filter((item: any) => item != null);
      const hasItems = renderedItems.length > 0;

      return (
        <ItemListContainer>
          <Menu ulRef={itemsParentRef} className="scrollable-items-menu">
            {hasItems ? (
              renderedItems
            ) : (
              <MenuItem disabled={true} text={<T id={'no_results'} />} />
            )}
          </Menu>
          {allowCreate && (
            <FixedCreateButton className="bp4-menu">
              <MenuItem
                icon="add"
                text={intl.get('create_new_vendor')}
                onClick={handleCreateItemClick}
              />
            </FixedCreateButton>
          )}
        </ItemListContainer>
      );
    },
    [allowCreate, handleCreateItemClick],
  );

  // Filter predicate - show all items when query is empty
  const itemPredicate = useCallback((query: any, item: any) => {
    if (!query) return true;
    const normalizedQuery = query.toLowerCase();
    const displayName = (item.display_name || '').toLowerCase();
    return displayName.includes(normalizedQuery);
  }, []);

  return (
    <FSelect
      name={name}
      items={items}
      textAccessor={'display_name'}
      labelAccessor={'formatted_balance'}
      valueAccessor={'id'}
      popoverProps={{ minimal: true, usePortal: false, inline: false }}
      itemListRenderer={itemListRenderer}
      itemPredicate={itemPredicate}
      scrollToActiveItem={false}
      {...restProps}
    />
  );
}

const ItemListContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 300px;

  .scrollable-items-menu {
    flex: 1;
    overflow-y: auto;
    max-height: 250px;
    margin: 0;
    padding: 5px 0;
  }
`;

const FixedCreateButton = styled.div`
  flex-shrink: 0;
  border-top: 1px solid rgba(16, 22, 26, 0.15);
  padding: 5px 0;

  .bp4-menu-item {
    margin: 0;
  }
`;

export const VendorsSelect = R.compose(withDrawerActions)(VendorsSelectRoot);
